import type { Locale } from '@/config/locales';
import type { Language } from '@/types';

export interface TranslationStrings {
  cartConfirm: string;
  cart: string;
  pay: string;
  homeTitle: string;
  searchPlaceholder: string;
  chooseFavorites: string;
  /**
   * 픽업 줄서기 게이트 매장 배지(admin#2172/menu#804) — 매장 카드에서 앱 픽업
   * 주문에 줄서기 called 확인이 필요함을 알린다.
   */
  pickupWaitlistBadge: string;
  resultsCount: string;
  loading: string;
  // #115: 에러 및 빈 상태 메시지
  errorLoadingStores: string;
  errorLoadingStoresMessage: string;
  noNearbyStores: string;
  noNearbyStoresMessage: string;
  qrScanTitle: string;
  tableNumber: string;
  date: string;
  time: string;
  menu: string;
  qty: string;
  price: string;
  selectTip: string;
  points: string;
  available: string;
  menuFirst: string;
  use: string;
  useAll: string;
  alwaysUseAll: string;
  availablePoints: string;
  giftCard: string;
  enterCode: string;
  apply: string;
  registerNewCard: string;
  subtotal: string;
  tax: string;
  tip: string;
  total: string;
  makePayment: string;
  storeStatus: {
    '영업중': string;
    '준비중': string;
    '마감': string;
  };
  sortBy: {
    rating: string;
    distance: string;
    name: string;
  };
  orders: {
    pickupOrdersTitle: string;
    /** 진행 중인 주문 통합 타이틀 (픽업+테이블). R1 claude 시정: ko/es/en 모두 채워져 있어 required. */
    activeOrdersTitle: string;
    /** 진행 중인 주문 조회 로딩 상태. */
    loadingActiveOrders: string;
    /** 진행 중인 주문 조회 실패 타이틀. */
    activeOrdersLoadError: string;
    /** 진행 중인 주문 조회 실패 기본 메시지. */
    activeOrdersLoadErrorMessage: string;
    /** 빈 상태 — 활성 주문 통합. R1 시정: required */
    noActiveOrders: string;
    /** #659: 인가 거절(401/403)로 표시물 0건일 때의 구분 빈 상태 안내 (silent empty 금지) */
    authRequiredNotice: string;
    /** 빈 상태 보조 — 전체 주문 내역 페이지 진입 링크 라벨. R1 시정: required */
    viewOrderHistory: string;
    /** 테이블 주문 카드의 테이블 번호 prefix (예: "Mesa A2"). R1 시정: required */
    tableLabel: string;
    pending: string;      // #78: API OrderStatus 지원
    confirmed: string;    // #78: API OrderStatus 지원
    preparing: string;
    ready: string;
    completed: string;
    cancelled: string;    // #78: API OrderStatus 지원
    failed: string;       // #78: API OrderStatus 지원
    order: string;
    preparationProgress: string;
    status: string;
    estimatedTime: string;
    readyNow: string;
    minutesRemaining: string;
    pickupLocation: string;
    openMap: string; // #239: 지도 열기 접근성
    items: string;
    noOrders: string; // #67: 빈 상태 메시지
    // 주문 타임라인/취소/환불 (#API동기화)
    timeline: string;
    cancelOrder: string;
    cancelConfirm: string;
    refundPending: string;
    refundPartial: string;
    refundFull: string;
    cancelledByCustomer: string;
    cancelledByStore: string;
    cancelledBySystem: string;
    orderTypePickup: string;
    orderTypeTable: string;
    /** C2 (#590) 주문내역 카드 결제상태 배지 라벨 */
    paymentStatus: {
      /** 미결제 (후불 pending) */
      unpaid: string;
      /** 결제완료 */
      paid: string;
      /** 결제실패 */
      failed: string;
      /** 결제취소 */
      cancelled: string;
      /** 처리중 */
      processing: string;
      /** 환불완료 */
      refunded: string;
      /** 부분환불 */
      partialRefund: string;
    };
  };
  categories: {
    all: string;
    burger: string;
    pizza: string;
    postre: string;
    pollo: string;
    came: string;
    sushi: string;
    marisco: string;
    taco: string;
    global: string;
    pan: string;
    vegano: string;
  };
  restaurants: Record<string, { name: string; cuisine: string }>;
}

/**
 * UI 전용 번역 — restaurants (브랜드 데모 데이터) 만 optional.
 *
 * #460 R2: 모든 15언어가 UI 액션 + 시스템 메시지 (storeStatus, sortBy, orders,
 * categories 포함) 을 *필수* 로 채워야 빌드 통과. restaurants 는 demo 브랜드
 * 데이터이므로 partial 허용 (en fallback 처리).
 */
export type UITranslation = Omit<TranslationStrings, 'restaurants'> & {
  restaurants?: Partial<TranslationStrings['restaurants']>;
};

/**
 * Translations 타입 — #460 (15언어 인프라).
 *
 * - ko/es/en: 풀 번역 (restaurants 포함)
 * - 12 신규 locale: UITranslation (UI 키 *필수*, restaurants optional fallback)
 * - 새 키 추가 시 15언어 모두 채워야 TS 빌드 통과 (Karpathy 박제: build gate)
 */
export type Translations = {
  ko: TranslationStrings;
  es: TranslationStrings;
  en: TranslationStrings;
} & Record<Exclude<Locale, 'ko' | 'es' | 'en'>, UITranslation>;

export const translations: Translations = {
  ko: {
    cartConfirm: '장바구니 확인',
    cart: '장바구니',
    pay: '결제하기',
    homeTitle: 'OLULO',
    searchPlaceholder: '매장 또는 음식 검색',
    chooseFavorites: '픽업 매장 찾기',
    pickupWaitlistBadge: '줄서기 후 주문',
    resultsCount: '개 결과',
    loading: '로딩 중...',
    errorLoadingStores: '매장 로드 오류',
    errorLoadingStoresMessage: '서버에 연결할 수 없습니다. 다시 시도해 주세요.',
    noNearbyStores: '근처에 매장이 없습니다',
    noNearbyStoresMessage: '현재 위치에서 매장을 찾을 수 없습니다. 다른 지역에서 검색하거나 나중에 다시 확인해 주세요.',
    qrScanTitle: 'QR 코드 스캔',
    tableNumber: '테이블',
    date: '날짜',
    time: '시간',
    menu: '메뉴',
    qty: '수량',
    price: '가격',
    selectTip: '팁 선택',
    points: '포인트',
    available: '사용 가능',
    menuFirst: '메뉴 우선',
    use: '사용',
    useAll: '전체 사용',
    alwaysUseAll: '항상 전체 사용',
    availablePoints: '사용 가능 포인트',
    giftCard: '기프트카드',
    enterCode: '코드 입력',
    apply: '적용',
    registerNewCard: '새 카드 등록',
    subtotal: '소계',
    tax: '세금',
    tip: '팁',
    total: '총 금액',
    makePayment: '결제하기',
    storeStatus: {
      '영업중': '영업중',
      '준비중': '준비중',
      '마감': '마감'
    },
    sortBy: {
      rating: '별점순',
      distance: '가까운순',
      name: '이름순'
    },
    orders: {
      pickupOrdersTitle: '픽업 주문',
      activeOrdersTitle: '진행 중인 주문',
      loadingActiveOrders: '진행 중인 주문을 불러오는 중입니다.',
      activeOrdersLoadError: '진행 중인 주문을 불러오지 못했습니다.',
      activeOrdersLoadErrorMessage: '잠시 후 다시 시도해 주세요.',
      noActiveOrders: '진행 중인 주문이 없습니다.',
      authRequiredNotice: '주문을 확인하려면 인증이 필요합니다.',
      viewOrderHistory: '전체 주문 내역 보기',
      tableLabel: '테이블',
      pending: '대기 중',
      confirmed: '확인됨',
      preparing: '준비 중',
      ready: '픽업 준비 완료',
      completed: '완료',
      cancelled: '취소됨',
      failed: '실패',
      order: '주문',
      preparationProgress: '준비 진행률',
      status: '상태',
      estimatedTime: '예상 시간',
      readyNow: '지금 픽업 가능합니다!',
      minutesRemaining: '분 남음',
      pickupLocation: '픽업 위치',
      openMap: '지도 열기',
      items: '항목',
      noOrders: '현재 진행 중인 픽업 주문이 없습니다.',
      timeline: '진행 이력',
      cancelOrder: '주문 취소',
      cancelConfirm: '정말로 이 주문을 취소하시겠습니까?',
      refundPending: '환불 대기',
      refundPartial: '부분 환불',
      refundFull: '환불 완료',
      cancelledByCustomer: '고객',
      cancelledByStore: '매장',
      cancelledBySystem: '시스템',
      orderTypePickup: '픽업',
      orderTypeTable: '테이블',
      paymentStatus: {
        unpaid: '미결제',
        paid: '결제완료',
        failed: '결제실패',
        cancelled: '결제취소',
        processing: '처리중',
        refunded: '환불완료',
        partialRefund: '부분환불',
      },
    },
    categories: {
      all: '모든 메뉴',
      burger: '버거',
      pizza: '피자',
      postre: '디저트',
      pollo: '닭고기',
      came: '고기',
      sushi: '스시',
      marisco: '해산물',
      taco: '타코',
      global: '글로벌',
      pan: '빵',
      vegano: '비건'
    },
    restaurants: {
      onTheBorder: {
        name: 'On the Border',
        cuisine: '는 멕시칸 요리와 타코, 웨스턴 음식을 즐길 수 있는 인기 식당입니다.'
      },
      pizzeriaItaliana: {
        name: 'Pizzería Italiana',
        cuisine: '는 이탈리안 피자와 유럽식 요리를 전문으로 하는 식당입니다.'
      },
      seoulBBQ: {
        name: 'Seoul BBQ House',
        cuisine: '는 한국식 바비큐와 아시안 요리를 제공하는 식당입니다.'
      },
      sushiZen: {
        name: 'Sushi Zen',
        cuisine: '은 일본식 스시와 아시안 퓨전 요리를 제공하는 프리미엄 식당입니다.'
      },
      mcdonalds: {
        name: '맥도날드',
        cuisine: '는 세계적인 패스트푸드 체인으로 버거와 감자튀김을 제공합니다.'
      },
      burgerKing: {
        name: 'Burger King Seoul',
        cuisine: '는 프리미엄 수제 버거와 감자튀김을 제공하는 버거 전문점입니다.'
      },
      shakeBurger: {
        name: 'Shake Burger',
        cuisine: '는 신선한 재료로 만든 버거와 밀크셰이크로 유명한 캐주얼 다이닝입니다.'
      },
      macBurger: {
        name: 'Mac Burger House',
        cuisine: '는 다양한 버거 옵션과 사이드 메뉴를 제공하는 패밀리 레스토랑입니다.'
      },
      classicBurger: {
        name: 'Classic Burger Co.',
        cuisine: '는 전통적인 미국식 버거와 컴포트 푸드를 전문으로 하는 식당입니다.'
      },
      megaBurger: {
        name: 'Mega Burger',
        cuisine: '는 대형 버거와 프리미엄 토핑으로 유명한 버거 체인점입니다.'
      },
      romanoPizza: {
        name: 'Romano Pizza',
        cuisine: '는 정통 나폴리 스타일 피자와 이탈리안 요리를 제공합니다.'
      },
      pepperoniPlace: {
        name: 'Pepperoni Place',
        cuisine: '는 다양한 토핑의 피자와 파스타를 전문으로 하는 이탈리안 레스토랑입니다.'
      },
      pizzaExpress: {
        name: 'Pizza Express',
        cuisine: '는 빠른 배달과 신선한 피자로 유명한 피자 전문점입니다.'
      },
      margheritaHouse: {
        name: 'Margherita House',
        cuisine: '는 클래식 마르게리타와 프리미엄 피자를 제공하는 피자리아입니다.'
      },
      sweetTreats: {
        name: 'Sweet Treats',
        cuisine: '는 수제 케이크와 디저트를 전문으로 하는 디저트 카페입니다.'
      },
      cakeMaster: {
        name: 'Cake Master',
        cuisine: '는 맞춤 케이크와 페이스트리로 유명한 베이커리입니다.'
      },
      dessertHeaven: {
        name: 'Dessert Heaven',
        cuisine: '는 프리미엄 디저트와 아이스크림을 제공하는 디저트 전문점입니다.'
      },
      chocolateDream: {
        name: 'Chocolate Dream',
        cuisine: '는 수제 초콜릿과 트러플을 전문으로 하는 초콜릿 부티크입니다.'
      },
      iceCreamParlor: {
        name: 'Ice Cream Parlor',
        cuisine: '는 다양한 맛의 아이스크림과 선데이를 제공하는 아이스크림 전문점입니다.'
      },
      crispyChicken: {
        name: 'Crispy Chicken',
        cuisine: '는 바삭한 프라이드 치킨과 윙으로 유명한 치킨 전문점입니다.'
      },
      seoulChicken: {
        name: 'Seoul Chicken',
        cuisine: '는 한국식 양념치킨과 후라이드치킨을 제공하는 치킨집입니다.'
      },
      spicyWings: {
        name: 'Spicy Wings',
        cuisine: '는 매운 치킨윙과 다양한 소스로 유명한 윙 전문점입니다.'
      },
      chickenHouse: {
        name: 'Chicken House',
        cuisine: '는 로스트 치킨과 그릴드 치킨을 전문으로 하는 치킨 레스토랑입니다.'
      },
      goldenChicken: {
        name: 'Golden Chicken',
        cuisine: '는 황금빛 크리스피 치킨과 사이드 메뉴를 제공하는 패밀리 레스토랑입니다.'
      },
      primeSteak: {
        name: 'Prime Steak House',
        cuisine: '는 프리미엄 스테이크와 고기 요리를 전문으로 하는 스테이크하우스입니다.'
      },
      meatLover: {
        name: 'Meat Lover',
        cuisine: '는 다양한 고기 요리와 바비큐를 제공하는 육류 전문 레스토랑입니다.'
      },
      grillMaster: {
        name: 'Grill Master',
        cuisine: '는 숯불 그릴 요리와 프리미엄 고기를 전문으로 하는 그릴 레스토랑입니다.'
      },
      ribeye: {
        name: 'Ribeye House',
        cuisine: '는 리브아이 스테이크와 와인을 전문으로 하는 고급 스테이크하우스입니다.'
      },
      wagyuPalace: {
        name: 'Wagyu Palace',
        cuisine: '는 와규 비프와 프리미엄 고기 요리를 제공하는 럭셔리 레스토랑입니다.'
      },
      oceanSushi: {
        name: 'Ocean Sushi',
        cuisine: '는 신선한 회와 스시를 전문으로 하는 일식 레스토랑입니다.'
      },
      tokyoSushi: {
        name: 'Tokyo Sushi Bar',
        cuisine: '는 도쿄 스타일 스시와 사시미를 제공하는 오마카세 전문점입니다.'
      },
      sashimiPlace: {
        name: 'Sashimi Place',
        cuisine: '는 프리미엄 사시미와 일본 요리를 전문으로 하는 일식당입니다.'
      },
      sushiMaster: {
        name: 'Sushi Master',
        cuisine: '는 장인이 만드는 수제 스시와 롤을 제공하는 스시 전문점입니다.'
      },
      seaBreeze: {
        name: 'Sea Breeze Seafood',
        cuisine: '는 신선한 해산물과 해물 요리를 전문으로 하는 씨푸드 레스토랑입니다.'
      },
      lobsterHouse: {
        name: 'Lobster House',
        cuisine: '는 랍스터와 게 요리를 전문으로 하는 프리미엄 해산물 레스토랑입니다.'
      },
      oysterBar: {
        name: 'Oyster Bar',
        cuisine: '는 신선한 굴과 조개 요리를 제공하는 오이스터 바입니다.'
      },
      fishMarket: {
        name: 'Fish Market',
        cuisine: '는 당일 잡은 신선한 생선 요리를 전문으로 하는 해산물 식당입니다.'
      },
      tacoFiesta: {
        name: 'Taco Fiesta',
        cuisine: '는 정통 멕시칸 타코와 부리또를 제공하는 멕시칸 레스토랑입니다.'
      },
      elTaco: {
        name: 'El Taco Loco',
        cuisine: '는 다양한 타코와 멕시칸 스트리트 푸드를 전문으로 하는 타코 전문점입니다.'
      },
      cantinaTaco: {
        name: 'Cantina Taco',
        cuisine: '는 전통 멕시칸 타코와 마가리타를 제공하는 멕시칸 칸티나입니다.'
      },
      artisanBread: {
        name: 'Artisan Bread',
        cuisine: '는 수제 빵과 페이스트리를 전문으로 하는 아티장 베이커리입니다.'
      },
      bakeryDelight: {
        name: 'Bakery Delight',
        cuisine: '는 갓 구운 빵과 케이크를 제공하는 전통 베이커리입니다.'
      },
      breadBasket: {
        name: 'Bread Basket',
        cuisine: '는 다양한 종류의 빵과 샌드위치를 전문으로 하는 베이커리 카페입니다.'
      },
      crustCraft: {
        name: 'Crust & Craft',
        cuisine: '는 프리미엄 크루아상과 페이스트리를 제공하는 프랑스 베이커리입니다.'
      },
      greenGarden: {
        name: 'Green Garden',
        cuisine: '는 유기농 채소와 비건 요리를 전문으로 하는 비건 레스토랑입니다.'
      },
      veganDelight: {
        name: 'Vegan Delight',
        cuisine: '는 건강한 비건 메뉴와 플랜트 베이스 요리를 제공하는 비건 카페입니다.'
      },
      plantPower: {
        name: 'Plant Power',
        cuisine: '는 영양가 높은 비건 요리와 스무디를 전문으로 하는 헬스 푸드점입니다.'
      },
      earthyEats: {
        name: 'Earthy Eats',
        cuisine: '는 자연 친화적인 비건 요리와 로푸드를 제공하는 에코 레스토랑입니다.'
      },
      worldFusion: {
        name: 'World Fusion',
        cuisine: '는 세계 각국의 퓨전 요리를 제공하는 인터내셔널 레스토랑입니다.'
      },
      globalTaste: {
        name: 'Global Taste',
        cuisine: '는 다양한 나라의 요리를 한 곳에서 즐길 수 있는 글로벌 푸드 코트입니다.'
      },
      continentalCafe: {
        name: 'Continental Cafe',
        cuisine: '는 유럽식 요리와 다양한 국제 메뉴를 제공하는 컨티넨탈 레스토랑입니다.'
      }
    }
  },
  es: {
    cartConfirm: 'Confirmar Carrito',
    cart: 'Carrito',
    pay: 'Pagar',
    homeTitle: 'OLULO',
    searchPlaceholder: 'Buscar tienda o comida',
    chooseFavorites: 'Buscar tienda con pedidos para recoger',
    pickupWaitlistBadge: 'Requiere fila para ordenar',
    resultsCount: 'resultados',
    loading: 'Cargando...',
    errorLoadingStores: 'Error al cargar tiendas',
    errorLoadingStoresMessage: 'No se pudo conectar con el servidor. Por favor, intenta de nuevo.',
    noNearbyStores: 'No hay tiendas cercanas',
    noNearbyStoresMessage: 'No encontramos tiendas en tu ubicación actual. Intenta buscar en otra zona o revisa más tarde.',
    qrScanTitle: 'Escanear código QR',
    tableNumber: 'Mesa',
    date: 'Fecha',
    time: 'Hora',
    menu: 'Menú',
    qty: 'Cant.',
    price: 'Precio',
    selectTip: 'Seleccionar propina',
    points: 'Puntos',
    available: 'Disponible',
    menuFirst: 'Menú primero',
    use: 'Usar',
    useAll: 'Usar todo',
    alwaysUseAll: 'Siempre usar todo',
    availablePoints: 'Puntos disponibles',
    giftCard: 'Tarjeta de regalo',
    enterCode: 'Ingresar código',
    apply: 'Aplicar',
    registerNewCard: 'Registrar nueva tarjeta',
    subtotal: 'Subtotal',
    tax: 'Impuesto',
    tip: 'Propina',
    total: 'Total',
    makePayment: 'Realizar pago',
    storeStatus: {
      '영업중': 'Abierto',
      '준비중': 'Preparando',
      '마감': 'Cerrado'
    },
    sortBy: {
      rating: 'Por calificación',
      distance: 'Por distancia',
      name: 'Por nombre'
    },
    orders: {
      pickupOrdersTitle: 'Pedidos para recoger',
      activeOrdersTitle: 'Pedidos en curso',
      loadingActiveOrders: 'Cargando pedidos en curso.',
      activeOrdersLoadError: 'No se pudieron cargar los pedidos en curso.',
      activeOrdersLoadErrorMessage: 'Inténtelo de nuevo en unos momentos.',
      noActiveOrders: 'No hay pedidos en curso.',
      authRequiredNotice: 'Se requiere autenticación para consultar sus pedidos.',
      viewOrderHistory: 'Ver historial de pedidos',
      tableLabel: 'Mesa',
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      preparing: 'Preparando',
      ready: 'Listo para recoger',
      completed: 'Completado',
      cancelled: 'Cancelado',
      failed: 'Fallido',
      order: 'Pedido',
      preparationProgress: 'Progreso de preparación',
      status: 'Estado',
      estimatedTime: 'Tiempo estimado',
      readyNow: '¡Listo para recoger ahora!',
      minutesRemaining: 'min restantes',
      pickupLocation: 'Ubicación para recoger el pedido',
      openMap: 'Abrir mapa',
      items: 'Artículos',
      noOrders: 'No hay pedidos para recoger en proceso.',
      timeline: 'Historial',
      cancelOrder: 'Cancelar pedido',
      cancelConfirm: '¿Está seguro de que desea cancelar este pedido?',
      refundPending: 'Reembolso pendiente',
      refundPartial: 'Reembolso parcial',
      refundFull: 'Reembolsado',
      cancelledByCustomer: 'Cliente',
      cancelledByStore: 'Tienda',
      cancelledBySystem: 'Sistema',
      orderTypePickup: 'Recoger',
      orderTypeTable: 'Mesa',
      paymentStatus: {
        unpaid: 'No pagado',
        paid: 'Pagado',
        failed: 'Pago fallido',
        cancelled: 'Pago cancelado',
        processing: 'Procesando',
        refunded: 'Reembolsado',
        partialRefund: 'Reembolso parcial',
      },
    },
    categories: {
      all: 'Todo',
      burger: 'Hamburguesa',
      pizza: 'Pizza',
      postre: 'Postre',
      pollo: 'Pollo',
      came: 'Carne',
      sushi: 'Sushi',
      marisco: 'Mariscos',
      taco: 'Taco',
      global: 'Internacional',
      pan: 'Pan',
      vegano: 'Vegano'
    },
    restaurants: {
      onTheBorder: {
        name: 'On the Border',
        cuisine: 'es un restaurante popular que ofrece cocina mexicana, tacos y comida occidental.'
      },
      pizzeriaItaliana: {
        name: 'Pizzería Italiana',
        cuisine: 'se especializa en pizza italiana y cocina europea.'
      },
      seoulBBQ: {
        name: 'Seoul BBQ House',
        cuisine: 'ofrece barbacoa coreana y cocina asiática.'
      },
      sushiZen: {
        name: 'Sushi Zen',
        cuisine: 'es un restaurante premium que ofrece sushi japonés y platos de fusión asiática.'
      },
      mcdonalds: {
        name: "McDonald's",
        cuisine: 'es una cadena global de comida rápida que ofrece hamburguesas y papas fritas.'
      },
      burgerKing: {
        name: 'Burger King Seoul',
        cuisine: 'es una hamburguesería premium que ofrece hamburguesas artesanales y papas fritas.'
      },
      shakeBurger: {
        name: 'Shake Burger',
        cuisine: 'es famoso por sus hamburguesas con ingredientes frescos y batidos.'
      },
      macBurger: {
        name: 'Mac Burger House',
        cuisine: 'es un restaurante familiar que ofrece diversas opciones de hamburguesas.'
      },
      classicBurger: {
        name: 'Classic Burger Co.',
        cuisine: 'se especializa en hamburguesas tradicionales americanas y comida casera.'
      },
      megaBurger: {
        name: 'Mega Burger',
        cuisine: 'es famoso por sus hamburguesas grandes y ingredientes premium.'
      },
      romanoPizza: {
        name: 'Romano Pizza',
        cuisine: 'ofrece pizza auténtica estilo napolitano y cocina italiana.'
      },
      pepperoniPlace: {
        name: 'Pepperoni Place',
        cuisine: 'es un restaurante italiano especializado en pizzas con varios ingredientes y pasta.'
      },
      pizzaExpress: {
        name: 'Pizza Express',
        cuisine: 'es famoso por entrega rápida y pizzas frescas.'
      },
      margheritaHouse: {
        name: 'Margherita House',
        cuisine: 'es una pizzería que ofrece margherita clásica y pizzas premium.'
      },
      sweetTreats: {
        name: 'Sweet Treats',
        cuisine: 'es un café de postres especializado en pasteles artesanales.'
      },
      cakeMaster: {
        name: 'Cake Master',
        cuisine: 'es famoso por pasteles personalizados y repostería.'
      },
      dessertHeaven: {
        name: 'Dessert Heaven',
        cuisine: 'es una tienda de postres que ofrece postres premium y helados.'
      },
      chocolateDream: {
        name: 'Chocolate Dream',
        cuisine: 'es una boutique de chocolate especializada en chocolate artesanal y trufas.'
      },
      iceCreamParlor: {
        name: 'Ice Cream Parlor',
        cuisine: 'es una heladería que ofrece varios sabores de helado y sundaes.'
      },
      crispyChicken: {
        name: 'Crispy Chicken',
        cuisine: 'es famoso por pollo frito crujiente y alitas.'
      },
      seoulChicken: {
        name: 'Seoul Chicken',
        cuisine: 'ofrece pollo estilo coreano marinado y frito.'
      },
      spicyWings: {
        name: 'Spicy Wings',
        cuisine: 'es famoso por alitas picantes y varias salsas.'
      },
      chickenHouse: {
        name: 'Chicken House',
        cuisine: 'se especializa en pollo asado y a la parrilla.'
      },
      goldenChicken: {
        name: 'Golden Chicken',
        cuisine: 'es un restaurante familiar que ofrece pollo dorado crujiente.'
      },
      primeSteak: {
        name: 'Prime Steak House',
        cuisine: 'es un asador especializado en steaks premium y platos de carne.'
      },
      meatLover: {
        name: 'Meat Lover',
        cuisine: 'es un restaurante especializado en carnes que ofrece varios platos de carne y barbacoa.'
      },
      grillMaster: {
        name: 'Grill Master',
        cuisine: 'se especializa en parrilla a carbón y carnes premium.'
      },
      ribeye: {
        name: 'Ribeye House',
        cuisine: 'es un asador de lujo especializado en ribeye y vinos.'
      },
      wagyuPalace: {
        name: 'Wagyu Palace',
        cuisine: 'es un restaurante de lujo que ofrece carne wagyu y platos premium.'
      },
      oceanSushi: {
        name: 'Ocean Sushi',
        cuisine: 'es un restaurante japonés especializado en sashimi fresco y sushi.'
      },
      tokyoSushi: {
        name: 'Tokyo Sushi Bar',
        cuisine: 'ofrece sushi estilo Tokyo y sashimi con experiencia omakase.'
      },
      sashimiPlace: {
        name: 'Sashimi Place',
        cuisine: 'se especializa en sashimi premium y cocina japonesa.'
      },
      sushiMaster: {
        name: 'Sushi Master',
        cuisine: 'ofrece sushi artesanal y rolls hechos por maestros.'
      },
      seaBreeze: {
        name: 'Sea Breeze Seafood',
        cuisine: 'es un restaurante de mariscos especializado en mariscos frescos.'
      },
      lobsterHouse: {
        name: 'Lobster House',
        cuisine: 'es un restaurante premium especializado en langosta y cangrejo.'
      },
      oysterBar: {
        name: 'Oyster Bar',
        cuisine: 'es un bar de ostras que ofrece ostras frescas y mariscos.'
      },
      fishMarket: {
        name: 'Fish Market',
        cuisine: 'se especializa en pescado fresco del día.'
      },
      tacoFiesta: {
        name: 'Taco Fiesta',
        cuisine: 'es un restaurante mexicano que ofrece tacos auténticos y burritos.'
      },
      elTaco: {
        name: 'El Taco Loco',
        cuisine: 'se especializa en varios tacos y comida callejera mexicana.'
      },
      cantinaTaco: {
        name: 'Cantina Taco',
        cuisine: 'es una cantina mexicana que ofrece tacos tradicionales y margaritas.'
      },
      artisanBread: {
        name: 'Artisan Bread',
        cuisine: 'es una panadería artesanal especializada en pan artesanal y repostería.'
      },
      bakeryDelight: {
        name: 'Bakery Delight',
        cuisine: 'es una panadería tradicional que ofrece pan recién horneado y pasteles.'
      },
      breadBasket: {
        name: 'Bread Basket',
        cuisine: 'es un café panadería especializado en varios tipos de pan y sándwiches.'
      },
      crustCraft: {
        name: 'Crust & Craft',
        cuisine: 'es una panadería francesa que ofrece croissants premium y repostería.'
      },
      greenGarden: {
        name: 'Green Garden',
        cuisine: 'es un restaurante vegano especializado en vegetales orgánicos y cocina vegana.'
      },
      veganDelight: {
        name: 'Vegan Delight',
        cuisine: 'es un café vegano que ofrece menús veganos saludables y platos a base de plantas.'
      },
      plantPower: {
        name: 'Plant Power',
        cuisine: 'se especializa en cocina vegana nutritiva y batidos.'
      },
      earthyEats: {
        name: 'Earthy Eats',
        cuisine: 'es un restaurante ecológico que ofrece cocina vegana ecológica y comida cruda.'
      },
      worldFusion: {
        name: 'World Fusion',
        cuisine: 'es un restaurante internacional que ofrece cocina fusión de todo el mundo.'
      },
      globalTaste: {
        name: 'Global Taste',
        cuisine: 'es un patio de comidas global donde puedes disfrutar de cocinas de varios países.'
      },
      continentalCafe: {
        name: 'Continental Cafe',
        cuisine: 'es un restaurante continental que ofrece cocina europea y menús internacionales.'
      }
    }
  },
  en: {
    cartConfirm: 'Confirm Cart',
    cart: 'Cart',
    pay: 'Pay',
    homeTitle: 'OLULO',
    searchPlaceholder: 'Search store or food',
    chooseFavorites: 'Find pickup store',
    pickupWaitlistBadge: 'Order after joining the line',
    resultsCount: 'results',
    loading: 'Loading...',
    errorLoadingStores: 'Error loading stores',
    errorLoadingStoresMessage: 'Could not connect to the server. Please try again.',
    noNearbyStores: 'No nearby stores',
    noNearbyStoresMessage: 'We couldn\'t find stores in your current location. Try searching in another area or check back later.',
    qrScanTitle: 'Scan QR Code',
    tableNumber: 'Table',
    date: 'Date',
    time: 'Time',
    menu: 'Menu',
    qty: 'Qty',
    price: 'Price',
    selectTip: 'Select tip',
    points: 'Points',
    available: 'Available',
    menuFirst: 'Menu first',
    use: 'Use',
    useAll: 'Use all',
    alwaysUseAll: 'Always use all',
    availablePoints: 'Available points',
    giftCard: 'Gift card',
    enterCode: 'Enter code',
    apply: 'Apply',
    registerNewCard: 'Register new card',
    subtotal: 'Subtotal',
    tax: 'Tax',
    tip: 'Tip',
    total: 'Total',
    makePayment: 'Make payment',
    storeStatus: {
      '영업중': 'Open',
      '준비중': 'Preparing',
      '마감': 'Closed'
    },
    sortBy: {
      rating: 'By rating',
      distance: 'By distance',
      name: 'By name'
    },
    orders: {
      pickupOrdersTitle: 'Pickup Orders',
      activeOrdersTitle: 'Active Orders',
      loadingActiveOrders: 'Loading active orders.',
      activeOrdersLoadError: 'Could not load active orders.',
      activeOrdersLoadErrorMessage: 'Please try again in a moment.',
      noActiveOrders: 'No active orders.',
      authRequiredNotice: 'Authentication is required to view your orders.',
      viewOrderHistory: 'View order history',
      tableLabel: 'Table',
      pending: 'Pending',
      confirmed: 'Confirmed',
      preparing: 'Preparing',
      ready: 'Ready for pickup',
      completed: 'Completed',
      cancelled: 'Cancelled',
      failed: 'Failed',
      order: 'Order',
      preparationProgress: 'Preparation progress',
      status: 'Status',
      estimatedTime: 'Estimated time',
      readyNow: 'Ready to pick up now!',
      minutesRemaining: 'min remaining',
      pickupLocation: 'Pickup location',
      openMap: 'Open map',
      items: 'Items',
      noOrders: 'No pickup orders in progress.',
      timeline: 'Timeline',
      cancelOrder: 'Cancel order',
      cancelConfirm: 'Are you sure you want to cancel this order?',
      refundPending: 'Refund pending',
      refundPartial: 'Partial refund',
      refundFull: 'Refunded',
      cancelledByCustomer: 'Customer',
      cancelledByStore: 'Store',
      cancelledBySystem: 'System',
      orderTypePickup: 'Pickup',
      orderTypeTable: 'Table',
      paymentStatus: {
        unpaid: 'Unpaid',
        paid: 'Paid',
        failed: 'Payment failed',
        cancelled: 'Payment cancelled',
        processing: 'Processing',
        refunded: 'Refunded',
        partialRefund: 'Partial refund',
      },
    },
    categories: {
      all: 'All',
      burger: 'Burger',
      pizza: 'Pizza',
      postre: 'Dessert',
      pollo: 'Chicken',
      came: 'Meat',
      sushi: 'Sushi',
      marisco: 'Seafood',
      taco: 'Taco',
      global: 'Global',
      pan: 'Bread',
      vegano: 'Vegan'
    },
    restaurants: {
      onTheBorder: {
        name: 'On the Border',
        cuisine: 'is a popular restaurant offering Mexican cuisine, tacos, and Western dishes.'
      },
      pizzeriaItaliana: {
        name: 'Pizzería Italiana',
        cuisine: 'specializes in Italian pizza and European cuisine.'
      },
      seoulBBQ: {
        name: 'Seoul BBQ House',
        cuisine: 'offers Korean BBQ and Asian cuisine.'
      },
      sushiZen: {
        name: 'Sushi Zen',
        cuisine: 'is a premium restaurant offering Japanese sushi and Asian fusion dishes.'
      },
      mcdonalds: {
        name: "McDonald's",
        cuisine: 'is a global fast food chain offering burgers and fries.'
      },
      burgerKing: {
        name: 'Burger King Seoul',
        cuisine: 'is a premium burger joint offering artisanal burgers and fries.'
      },
      shakeBurger: {
        name: 'Shake Burger',
        cuisine: 'is famous for fresh ingredient burgers and milkshakes.'
      },
      macBurger: {
        name: 'Mac Burger House',
        cuisine: 'is a family restaurant offering various burger options.'
      },
      classicBurger: {
        name: 'Classic Burger Co.',
        cuisine: 'specializes in traditional American burgers and comfort food.'
      },
      megaBurger: {
        name: 'Mega Burger',
        cuisine: 'is famous for large burgers and premium toppings.'
      },
      romanoPizza: {
        name: 'Romano Pizza',
        cuisine: 'offers authentic Neapolitan style pizza and Italian cuisine.'
      },
      pepperoniPlace: {
        name: 'Pepperoni Place',
        cuisine: 'is an Italian restaurant specializing in various topping pizzas and pasta.'
      },
      pizzaExpress: {
        name: 'Pizza Express',
        cuisine: 'is famous for fast delivery and fresh pizzas.'
      },
      margheritaHouse: {
        name: 'Margherita House',
        cuisine: 'is a pizzeria offering classic margherita and premium pizzas.'
      },
      sweetTreats: {
        name: 'Sweet Treats',
        cuisine: 'is a dessert cafe specializing in handmade cakes.'
      },
      cakeMaster: {
        name: 'Cake Master',
        cuisine: 'is famous for custom cakes and pastries.'
      },
      dessertHeaven: {
        name: 'Dessert Heaven',
        cuisine: 'is a dessert shop offering premium desserts and ice cream.'
      },
      chocolateDream: {
        name: 'Chocolate Dream',
        cuisine: 'is a chocolate boutique specializing in handmade chocolate and truffles.'
      },
      iceCreamParlor: {
        name: 'Ice Cream Parlor',
        cuisine: 'is an ice cream shop offering various flavors and sundaes.'
      },
      crispyChicken: {
        name: 'Crispy Chicken',
        cuisine: 'is famous for crispy fried chicken and wings.'
      },
      seoulChicken: {
        name: 'Seoul Chicken',
        cuisine: 'offers Korean-style marinated and fried chicken.'
      },
      spicyWings: {
        name: 'Spicy Wings',
        cuisine: 'is famous for spicy chicken wings and various sauces.'
      },
      chickenHouse: {
        name: 'Chicken House',
        cuisine: 'specializes in roasted and grilled chicken.'
      },
      goldenChicken: {
        name: 'Golden Chicken',
        cuisine: 'is a family restaurant offering golden crispy chicken.'
      },
      primeSteak: {
        name: 'Prime Steak House',
        cuisine: 'is a steakhouse specializing in premium steaks and meat dishes.'
      },
      meatLover: {
        name: 'Meat Lover',
        cuisine: 'is a meat specialty restaurant offering various meat dishes and BBQ.'
      },
      grillMaster: {
        name: 'Grill Master',
        cuisine: 'specializes in charcoal grill cooking and premium meats.'
      },
      ribeye: {
        name: 'Ribeye House',
        cuisine: 'is a luxury steakhouse specializing in ribeye steaks and wines.'
      },
      wagyuPalace: {
        name: 'Wagyu Palace',
        cuisine: 'is a luxury restaurant offering wagyu beef and premium meat dishes.'
      },
      oceanSushi: {
        name: 'Ocean Sushi',
        cuisine: 'is a Japanese restaurant specializing in fresh sashimi and sushi.'
      },
      tokyoSushi: {
        name: 'Tokyo Sushi Bar',
        cuisine: 'offers Tokyo-style sushi and sashimi with omakase experience.'
      },
      sashimiPlace: {
        name: 'Sashimi Place',
        cuisine: 'specializes in premium sashimi and Japanese cuisine.'
      },
      sushiMaster: {
        name: 'Sushi Master',
        cuisine: 'offers artisanal sushi and rolls made by masters.'
      },
      seaBreeze: {
        name: 'Sea Breeze Seafood',
        cuisine: 'is a seafood restaurant specializing in fresh seafood.'
      },
      lobsterHouse: {
        name: 'Lobster House',
        cuisine: 'is a premium restaurant specializing in lobster and crab.'
      },
      oysterBar: {
        name: 'Oyster Bar',
        cuisine: 'is an oyster bar offering fresh oysters and shellfish.'
      },
      fishMarket: {
        name: 'Fish Market',
        cuisine: 'specializes in fresh catch of the day.'
      },
      tacoFiesta: {
        name: 'Taco Fiesta',
        cuisine: 'is a Mexican restaurant offering authentic tacos and burritos.'
      },
      elTaco: {
        name: 'El Taco Loco',
        cuisine: 'specializes in various tacos and Mexican street food.'
      },
      cantinaTaco: {
        name: 'Cantina Taco',
        cuisine: 'is a Mexican cantina offering traditional tacos and margaritas.'
      },
      artisanBread: {
        name: 'Artisan Bread',
        cuisine: 'is an artisan bakery specializing in handcrafted bread and pastries.'
      },
      bakeryDelight: {
        name: 'Bakery Delight',
        cuisine: 'is a traditional bakery offering freshly baked bread and cakes.'
      },
      breadBasket: {
        name: 'Bread Basket',
        cuisine: 'is a bakery cafe specializing in various types of bread and sandwiches.'
      },
      crustCraft: {
        name: 'Crust & Craft',
        cuisine: 'is a French bakery offering premium croissants and pastries.'
      },
      greenGarden: {
        name: 'Green Garden',
        cuisine: 'is a vegan restaurant specializing in organic vegetables and vegan cuisine.'
      },
      veganDelight: {
        name: 'Vegan Delight',
        cuisine: 'is a vegan cafe offering healthy vegan menus and plant-based dishes.'
      },
      plantPower: {
        name: 'Plant Power',
        cuisine: 'specializes in nutritious vegan cuisine and smoothies.'
      },
      earthyEats: {
        name: 'Earthy Eats',
        cuisine: 'is an eco restaurant offering eco-friendly vegan cuisine and raw food.'
      },
      worldFusion: {
        name: 'World Fusion',
        cuisine: 'is an international restaurant offering fusion cuisine from around the world.'
      },
      globalTaste: {
        name: 'Global Taste',
        cuisine: 'is a global food court where you can enjoy cuisines from various countries.'
      },
      continentalCafe: {
        name: 'Continental Cafe',
        cuisine: 'is a continental restaurant offering European cuisine and international menus.'
      }
    }
  },

  // #460 신규 12 locales — UI 액션 + 시스템 메시지 *전체 키 필수* (R2: build gate).
  // restaurants 는 브랜드 demo data 로 en fallback (UITranslation 타입 정합).

  ja: {
    cartConfirm: 'カート確認',
    cart: 'カート',
    pay: '支払う',
    homeTitle: 'OLULO',
    searchPlaceholder: '店舗または料理を検索',
    chooseFavorites: 'ピックアップ店舗を探す',
    pickupWaitlistBadge: '順番待ち後に注文',
    resultsCount: '件の結果',
    loading: '読み込み中...',
    errorLoadingStores: '店舗の読み込みエラー',
    errorLoadingStoresMessage: 'サーバーに接続できません。再試行してください。',
    noNearbyStores: '近くに店舗がありません',
    noNearbyStoresMessage: '現在地で店舗が見つかりません。別の地域で検索するか、後で確認してください。',
    qrScanTitle: 'QRコードスキャン',
    tableNumber: 'テーブル',
    date: '日付',
    time: '時間',
    menu: 'メニュー',
    qty: '数量',
    price: '価格',
    selectTip: 'チップ選択',
    points: 'ポイント',
    available: '利用可能',
    menuFirst: 'メニュー優先',
    use: '使用',
    useAll: '全て使用',
    alwaysUseAll: '常に全て使用',
    availablePoints: '利用可能ポイント',
    giftCard: 'ギフトカード',
    enterCode: 'コード入力',
    apply: '適用',
    registerNewCard: '新規カード登録',
    subtotal: '小計',
    tax: '税金',
    tip: 'チップ',
    total: '合計',
    makePayment: '支払う',
    storeStatus: {
      '영업중': '営業中',
      '준비중': '準備中',
      '마감': '閉店',
    },
    sortBy: {
      rating: '評価順',
      distance: '距離順',
      name: '名前順',
    },
    orders: {
      pickupOrdersTitle: 'ピックアップ注文',
      activeOrdersTitle: '進行中の注文',
      loadingActiveOrders: '進行中の注文を読み込んでいます。',
      activeOrdersLoadError: '進行中の注文を読み込めませんでした。',
      activeOrdersLoadErrorMessage: 'しばらくしてからもう一度お試しください。',
      noActiveOrders: '進行中の注文はありません。',
      authRequiredNotice: '注文を確認するには認証が必要です。',
      viewOrderHistory: '注文履歴を見る',
      tableLabel: 'テーブル',
      pending: '保留中',
      confirmed: '確認済み',
      preparing: '準備中',
      ready: 'ピックアップ準備完了',
      completed: '完了',
      cancelled: 'キャンセル済み',
      failed: '失敗',
      order: '注文',
      preparationProgress: '準備進捗',
      status: 'ステータス',
      estimatedTime: '予想時間',
      readyNow: '今すぐピックアップ可能!',
      minutesRemaining: '分残り',
      pickupLocation: 'ピックアップ場所',
      openMap: '地図を開く',
      items: 'アイテム',
      noOrders: '進行中のピックアップ注文はありません。',
      timeline: '履歴',
      cancelOrder: '注文をキャンセル',
      cancelConfirm: 'この注文をキャンセルしてもよろしいですか?',
      refundPending: '返金保留中',
      refundPartial: '一部返金',
      refundFull: '返金完了',
      cancelledByCustomer: 'お客様',
      cancelledByStore: '店舗',
      cancelledBySystem: 'システム',
      orderTypePickup: 'ピックアップ',
      orderTypeTable: 'テーブル',
      paymentStatus: {
        unpaid: '未払い',
        paid: '支払い済み',
        failed: '支払い失敗',
        cancelled: '支払いキャンセル',
        processing: '処理中',
        refunded: '返金済み',
        partialRefund: '一部返金',
      },
    },
    categories: {
      all: 'すべて',
      burger: 'バーガー',
      pizza: 'ピザ',
      postre: 'デザート',
      pollo: 'チキン',
      came: '肉料理',
      sushi: '寿司',
      marisco: '海鮮',
      taco: 'タコス',
      global: 'グローバル',
      pan: 'パン',
      vegano: 'ヴィーガン',
    },
  },

  zh: {
    cartConfirm: '购物车确认',
    cart: '购物车',
    pay: '支付',
    homeTitle: 'OLULO',
    searchPlaceholder: '搜索店铺或菜品',
    chooseFavorites: '查找取餐店',
    pickupWaitlistBadge: '排队后下单',
    resultsCount: '个结果',
    loading: '加载中...',
    errorLoadingStores: '加载店铺错误',
    errorLoadingStoresMessage: '无法连接服务器,请重试。',
    noNearbyStores: '附近没有店铺',
    noNearbyStoresMessage: '当前位置找不到店铺。请尝试其他地区或稍后再试。',
    qrScanTitle: '二维码扫描',
    tableNumber: '桌号',
    date: '日期',
    time: '时间',
    menu: '菜单',
    qty: '数量',
    price: '价格',
    selectTip: '选择小费',
    points: '积分',
    available: '可用',
    menuFirst: '菜单优先',
    use: '使用',
    useAll: '全部使用',
    alwaysUseAll: '始终全部使用',
    availablePoints: '可用积分',
    giftCard: '礼品卡',
    enterCode: '输入代码',
    apply: '应用',
    registerNewCard: '注册新卡',
    subtotal: '小计',
    tax: '税',
    tip: '小费',
    total: '总计',
    makePayment: '支付',
    storeStatus: {
      '영업중': '营业中',
      '준비중': '准备中',
      '마감': '已关闭',
    },
    sortBy: {
      rating: '按评分',
      distance: '按距离',
      name: '按名称',
    },
    orders: {
      pickupOrdersTitle: '取餐订单',
      activeOrdersTitle: '进行中的订单',
      loadingActiveOrders: '正在加载进行中的订单。',
      activeOrdersLoadError: '无法加载进行中的订单。',
      activeOrdersLoadErrorMessage: '请稍后重试。',
      noActiveOrders: '暂无进行中的订单。',
      authRequiredNotice: '查看订单需要进行验证。',
      viewOrderHistory: '查看订单历史',
      tableLabel: '桌号',
      pending: '待处理',
      confirmed: '已确认',
      preparing: '准备中',
      ready: '可以取餐',
      completed: '已完成',
      cancelled: '已取消',
      failed: '失败',
      order: '订单',
      preparationProgress: '准备进度',
      status: '状态',
      estimatedTime: '预计时间',
      readyNow: '现在可以取餐了!',
      minutesRemaining: '分钟后',
      pickupLocation: '取餐位置',
      openMap: '打开地图',
      items: '商品',
      noOrders: '暂无进行中的取餐订单。',
      timeline: '时间轴',
      cancelOrder: '取消订单',
      cancelConfirm: '确定要取消此订单吗?',
      refundPending: '退款待处理',
      refundPartial: '部分退款',
      refundFull: '已退款',
      cancelledByCustomer: '客户',
      cancelledByStore: '店铺',
      cancelledBySystem: '系统',
      orderTypePickup: '取餐',
      orderTypeTable: '堂食',
      paymentStatus: {
        unpaid: '未付款',
        paid: '已付款',
        failed: '付款失败',
        cancelled: '付款取消',
        processing: '处理中',
        refunded: '已退款',
        partialRefund: '部分退款',
      },
    },
    categories: {
      all: '全部',
      burger: '汉堡',
      pizza: '披萨',
      postre: '甜点',
      pollo: '鸡肉',
      came: '肉类',
      sushi: '寿司',
      marisco: '海鲜',
      taco: '塔可',
      global: '国际',
      pan: '面包',
      vegano: '素食',
    },
  },

  pt: {
    cartConfirm: 'Confirmar Carrinho',
    cart: 'Carrinho',
    pay: 'Pagar',
    homeTitle: 'OLULO',
    searchPlaceholder: 'Buscar loja ou comida',
    chooseFavorites: 'Encontrar loja para retirada',
    pickupWaitlistBadge: 'Pedir após entrar na fila',
    resultsCount: 'resultados',
    loading: 'Carregando...',
    errorLoadingStores: 'Erro ao carregar lojas',
    errorLoadingStoresMessage: 'Não foi possível conectar ao servidor. Tente novamente.',
    noNearbyStores: 'Nenhuma loja próxima',
    noNearbyStoresMessage: 'Não foi possível encontrar lojas na sua localização atual.',
    qrScanTitle: 'Escanear Código QR',
    tableNumber: 'Mesa',
    date: 'Data',
    time: 'Hora',
    menu: 'Menu',
    qty: 'Qtd',
    price: 'Preço',
    selectTip: 'Selecionar gorjeta',
    points: 'Pontos',
    available: 'Disponível',
    menuFirst: 'Menu primeiro',
    use: 'Usar',
    useAll: 'Usar tudo',
    alwaysUseAll: 'Sempre usar tudo',
    availablePoints: 'Pontos disponíveis',
    giftCard: 'Cartão-presente',
    enterCode: 'Inserir código',
    apply: 'Aplicar',
    registerNewCard: 'Registrar novo cartão',
    subtotal: 'Subtotal',
    tax: 'Imposto',
    tip: 'Gorjeta',
    total: 'Total',
    makePayment: 'Pagar',
    storeStatus: {
      '영업중': 'Aberto',
      '준비중': 'Preparando',
      '마감': 'Fechado',
    },
    sortBy: {
      rating: 'Por avaliação',
      distance: 'Por distância',
      name: 'Por nome',
    },
    orders: {
      pickupOrdersTitle: 'Pedidos para retirada',
      activeOrdersTitle: 'Pedidos em andamento',
      loadingActiveOrders: 'Carregando pedidos em andamento.',
      activeOrdersLoadError: 'Não foi possível carregar os pedidos em andamento.',
      activeOrdersLoadErrorMessage: 'Tente novamente em instantes.',
      noActiveOrders: 'Nenhum pedido em andamento.',
      authRequiredNotice: 'É necessária autenticação para consultar seus pedidos.',
      viewOrderHistory: 'Ver histórico de pedidos',
      tableLabel: 'Mesa',
      pending: 'Pendente',
      confirmed: 'Confirmado',
      preparing: 'Preparando',
      ready: 'Pronto para retirada',
      completed: 'Concluído',
      cancelled: 'Cancelado',
      failed: 'Falhou',
      order: 'Pedido',
      preparationProgress: 'Progresso de preparação',
      status: 'Status',
      estimatedTime: 'Tempo estimado',
      readyNow: 'Pronto para retirar agora!',
      minutesRemaining: 'min restantes',
      pickupLocation: 'Local de retirada',
      openMap: 'Abrir mapa',
      items: 'Itens',
      noOrders: 'Nenhum pedido para retirada em andamento.',
      timeline: 'Histórico',
      cancelOrder: 'Cancelar pedido',
      cancelConfirm: 'Tem certeza de que deseja cancelar este pedido?',
      refundPending: 'Reembolso pendente',
      refundPartial: 'Reembolso parcial',
      refundFull: 'Reembolsado',
      cancelledByCustomer: 'Cliente',
      cancelledByStore: 'Loja',
      cancelledBySystem: 'Sistema',
      orderTypePickup: 'Retirada',
      orderTypeTable: 'Mesa',
      paymentStatus: {
        unpaid: 'Não pago',
        paid: 'Pago',
        failed: 'Pagamento falhou',
        cancelled: 'Pagamento cancelado',
        processing: 'Processando',
        refunded: 'Reembolsado',
        partialRefund: 'Reembolso parcial',
      },
    },
    categories: {
      all: 'Todos',
      burger: 'Hambúrguer',
      pizza: 'Pizza',
      postre: 'Sobremesa',
      pollo: 'Frango',
      came: 'Carne',
      sushi: 'Sushi',
      marisco: 'Frutos do mar',
      taco: 'Taco',
      global: 'Internacional',
      pan: 'Pão',
      vegano: 'Vegano',
    },
  },

  fr: {
    cartConfirm: 'Confirmer le panier',
    cart: 'Panier',
    pay: 'Payer',
    homeTitle: 'OLULO',
    searchPlaceholder: 'Rechercher un restaurant ou un plat',
    chooseFavorites: 'Trouver un restaurant à emporter',
    pickupWaitlistBadge: 'Commander après la file',
    resultsCount: 'résultats',
    loading: 'Chargement...',
    errorLoadingStores: 'Erreur de chargement des restaurants',
    errorLoadingStoresMessage: 'Impossible de se connecter au serveur. Veuillez réessayer.',
    noNearbyStores: 'Aucun restaurant à proximité',
    noNearbyStoresMessage: 'Aucun restaurant trouvé à votre emplacement actuel.',
    qrScanTitle: 'Scanner le code QR',
    tableNumber: 'Table',
    date: 'Date',
    time: 'Heure',
    menu: 'Menu',
    qty: 'Qté',
    price: 'Prix',
    selectTip: 'Sélectionner un pourboire',
    points: 'Points',
    available: 'Disponible',
    menuFirst: 'Menu en premier',
    use: 'Utiliser',
    useAll: 'Tout utiliser',
    alwaysUseAll: 'Toujours tout utiliser',
    availablePoints: 'Points disponibles',
    giftCard: 'Carte-cadeau',
    enterCode: 'Entrer le code',
    apply: 'Appliquer',
    registerNewCard: 'Enregistrer une nouvelle carte',
    subtotal: 'Sous-total',
    tax: 'Taxe',
    tip: 'Pourboire',
    total: 'Total',
    makePayment: 'Payer',
    storeStatus: {
      '영업중': 'Ouvert',
      '준비중': 'En préparation',
      '마감': 'Fermé',
    },
    sortBy: {
      rating: 'Par note',
      distance: 'Par distance',
      name: 'Par nom',
    },
    orders: {
      pickupOrdersTitle: 'Commandes à emporter',
      activeOrdersTitle: 'Commandes en cours',
      loadingActiveOrders: 'Chargement des commandes en cours.',
      activeOrdersLoadError: 'Impossible de charger les commandes en cours.',
      activeOrdersLoadErrorMessage: 'Veuillez réessayer dans un instant.',
      noActiveOrders: 'Aucune commande en cours.',
      authRequiredNotice: 'Une authentification est requise pour consulter vos commandes.',
      viewOrderHistory: 'Voir l\'historique des commandes',
      tableLabel: 'Table',
      pending: 'En attente',
      confirmed: 'Confirmée',
      preparing: 'En préparation',
      ready: 'Prête à emporter',
      completed: 'Terminée',
      cancelled: 'Annulée',
      failed: 'Échouée',
      order: 'Commande',
      preparationProgress: 'Progression de la préparation',
      status: 'Statut',
      estimatedTime: 'Temps estimé',
      readyNow: 'Prête à emporter maintenant !',
      minutesRemaining: 'min restantes',
      pickupLocation: 'Lieu de retrait',
      openMap: 'Ouvrir la carte',
      items: 'Articles',
      noOrders: 'Aucune commande à emporter en cours.',
      timeline: 'Historique',
      cancelOrder: 'Annuler la commande',
      cancelConfirm: 'Êtes-vous sûr de vouloir annuler cette commande ?',
      refundPending: 'Remboursement en attente',
      refundPartial: 'Remboursement partiel',
      refundFull: 'Remboursée',
      cancelledByCustomer: 'Client',
      cancelledByStore: 'Restaurant',
      cancelledBySystem: 'Système',
      orderTypePickup: 'À emporter',
      orderTypeTable: 'Sur place',
      paymentStatus: {
        unpaid: 'Non payé',
        paid: 'Payé',
        failed: 'Paiement échoué',
        cancelled: 'Paiement annulé',
        processing: 'En cours',
        refunded: 'Remboursé',
        partialRefund: 'Remboursement partiel',
      },
    },
    categories: {
      all: 'Tout',
      burger: 'Burger',
      pizza: 'Pizza',
      postre: 'Dessert',
      pollo: 'Poulet',
      came: 'Viande',
      sushi: 'Sushi',
      marisco: 'Fruits de mer',
      taco: 'Taco',
      global: 'International',
      pan: 'Pain',
      vegano: 'Végan',
    },
  },

  de: {
    cartConfirm: 'Warenkorb bestätigen',
    cart: 'Warenkorb',
    pay: 'Bezahlen',
    homeTitle: 'OLULO',
    searchPlaceholder: 'Restaurant oder Gericht suchen',
    chooseFavorites: 'Abholrestaurant finden',
    pickupWaitlistBadge: 'Bestellung nach Warteschlange',
    resultsCount: 'Ergebnisse',
    loading: 'Laden...',
    errorLoadingStores: 'Fehler beim Laden der Restaurants',
    errorLoadingStoresMessage: 'Verbindung zum Server fehlgeschlagen. Bitte erneut versuchen.',
    noNearbyStores: 'Keine Restaurants in der Nähe',
    noNearbyStoresMessage: 'Keine Restaurants an Ihrem aktuellen Standort gefunden.',
    qrScanTitle: 'QR-Code scannen',
    tableNumber: 'Tisch',
    date: 'Datum',
    time: 'Zeit',
    menu: 'Speisekarte',
    qty: 'Menge',
    price: 'Preis',
    selectTip: 'Trinkgeld auswählen',
    points: 'Punkte',
    available: 'Verfügbar',
    menuFirst: 'Menü zuerst',
    use: 'Verwenden',
    useAll: 'Alles verwenden',
    alwaysUseAll: 'Immer alles verwenden',
    availablePoints: 'Verfügbare Punkte',
    giftCard: 'Geschenkkarte',
    enterCode: 'Code eingeben',
    apply: 'Anwenden',
    registerNewCard: 'Neue Karte registrieren',
    subtotal: 'Zwischensumme',
    tax: 'Steuer',
    tip: 'Trinkgeld',
    total: 'Gesamt',
    makePayment: 'Bezahlen',
    storeStatus: {
      '영업중': 'Geöffnet',
      '준비중': 'In Vorbereitung',
      '마감': 'Geschlossen',
    },
    sortBy: {
      rating: 'Nach Bewertung',
      distance: 'Nach Entfernung',
      name: 'Nach Name',
    },
    orders: {
      pickupOrdersTitle: 'Abholbestellungen',
      activeOrdersTitle: 'Aktive Bestellungen',
      loadingActiveOrders: 'Aktive Bestellungen werden geladen.',
      activeOrdersLoadError: 'Aktive Bestellungen konnten nicht geladen werden.',
      activeOrdersLoadErrorMessage: 'Bitte versuchen Sie es gleich erneut.',
      noActiveOrders: 'Keine aktiven Bestellungen.',
      authRequiredNotice: 'Zur Anzeige Ihrer Bestellungen ist eine Authentifizierung erforderlich.',
      viewOrderHistory: 'Bestellverlauf anzeigen',
      tableLabel: 'Tisch',
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      preparing: 'In Vorbereitung',
      ready: 'Zur Abholung bereit',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert',
      failed: 'Fehlgeschlagen',
      order: 'Bestellung',
      preparationProgress: 'Zubereitungsfortschritt',
      status: 'Status',
      estimatedTime: 'Geschätzte Zeit',
      readyNow: 'Jetzt zur Abholung bereit!',
      minutesRemaining: 'Min verbleibend',
      pickupLocation: 'Abholort',
      openMap: 'Karte öffnen',
      items: 'Artikel',
      noOrders: 'Keine Abholbestellungen in Bearbeitung.',
      timeline: 'Verlauf',
      cancelOrder: 'Bestellung stornieren',
      cancelConfirm: 'Möchten Sie diese Bestellung wirklich stornieren?',
      refundPending: 'Rückerstattung ausstehend',
      refundPartial: 'Teilrückerstattung',
      refundFull: 'Erstattet',
      cancelledByCustomer: 'Kunde',
      cancelledByStore: 'Restaurant',
      cancelledBySystem: 'System',
      orderTypePickup: 'Abholung',
      orderTypeTable: 'Tisch',
      paymentStatus: {
        unpaid: 'Nicht bezahlt',
        paid: 'Bezahlt',
        failed: 'Zahlung fehlgeschlagen',
        cancelled: 'Zahlung storniert',
        processing: 'Wird verarbeitet',
        refunded: 'Erstattet',
        partialRefund: 'Teilerstattung',
      },
    },
    categories: {
      all: 'Alle',
      burger: 'Burger',
      pizza: 'Pizza',
      postre: 'Dessert',
      pollo: 'Hähnchen',
      came: 'Fleisch',
      sushi: 'Sushi',
      marisco: 'Meeresfrüchte',
      taco: 'Taco',
      global: 'International',
      pan: 'Brot',
      vegano: 'Vegan',
    },
  },

  it: {
    cartConfirm: 'Conferma carrello',
    cart: 'Carrello',
    pay: 'Paga',
    homeTitle: 'OLULO',
    searchPlaceholder: 'Cerca ristorante o piatto',
    chooseFavorites: 'Trova ristorante per asporto',
    pickupWaitlistBadge: 'Ordina dopo la fila',
    resultsCount: 'risultati',
    loading: 'Caricamento...',
    errorLoadingStores: 'Errore caricamento ristoranti',
    errorLoadingStoresMessage: 'Impossibile connettersi al server. Riprova.',
    noNearbyStores: 'Nessun ristorante nelle vicinanze',
    noNearbyStoresMessage: 'Nessun ristorante trovato nella posizione attuale.',
    qrScanTitle: 'Scansiona codice QR',
    tableNumber: 'Tavolo',
    date: 'Data',
    time: 'Ora',
    menu: 'Menu',
    qty: 'Qtà',
    price: 'Prezzo',
    selectTip: 'Seleziona mancia',
    points: 'Punti',
    available: 'Disponibile',
    menuFirst: 'Menu prima',
    use: 'Usa',
    useAll: 'Usa tutto',
    alwaysUseAll: 'Usa sempre tutto',
    availablePoints: 'Punti disponibili',
    giftCard: 'Carta regalo',
    enterCode: 'Inserisci codice',
    apply: 'Applica',
    registerNewCard: 'Registra nuova carta',
    subtotal: 'Subtotale',
    tax: 'Tasse',
    tip: 'Mancia',
    total: 'Totale',
    makePayment: 'Paga',
    storeStatus: {
      '영업중': 'Aperto',
      '준비중': 'In preparazione',
      '마감': 'Chiuso',
    },
    sortBy: {
      rating: 'Per valutazione',
      distance: 'Per distanza',
      name: 'Per nome',
    },
    orders: {
      pickupOrdersTitle: 'Ordini da asporto',
      activeOrdersTitle: 'Ordini attivi',
      loadingActiveOrders: 'Caricamento degli ordini attivi.',
      activeOrdersLoadError: 'Impossibile caricare gli ordini attivi.',
      activeOrdersLoadErrorMessage: 'Riprova tra qualche istante.',
      noActiveOrders: 'Nessun ordine attivo.',
      authRequiredNotice: 'È richiesta l\'autenticazione per consultare i tuoi ordini.',
      viewOrderHistory: 'Visualizza cronologia ordini',
      tableLabel: 'Tavolo',
      pending: 'In sospeso',
      confirmed: 'Confermato',
      preparing: 'In preparazione',
      ready: 'Pronto per il ritiro',
      completed: 'Completato',
      cancelled: 'Annullato',
      failed: 'Fallito',
      order: 'Ordine',
      preparationProgress: 'Avanzamento preparazione',
      status: 'Stato',
      estimatedTime: 'Tempo stimato',
      readyNow: 'Pronto per il ritiro adesso!',
      minutesRemaining: 'min rimanenti',
      pickupLocation: 'Luogo di ritiro',
      openMap: 'Apri mappa',
      items: 'Articoli',
      noOrders: 'Nessun ordine da asporto in corso.',
      timeline: 'Cronologia',
      cancelOrder: 'Annulla ordine',
      cancelConfirm: 'Sei sicuro di voler annullare questo ordine?',
      refundPending: 'Rimborso in sospeso',
      refundPartial: 'Rimborso parziale',
      refundFull: 'Rimborsato',
      cancelledByCustomer: 'Cliente',
      cancelledByStore: 'Ristorante',
      cancelledBySystem: 'Sistema',
      orderTypePickup: 'Asporto',
      orderTypeTable: 'Al tavolo',
      paymentStatus: {
        unpaid: 'Non pagato',
        paid: 'Pagato',
        failed: 'Pagamento fallito',
        cancelled: 'Pagamento annullato',
        processing: 'In elaborazione',
        refunded: 'Rimborsato',
        partialRefund: 'Rimborso parziale',
      },
    },
    categories: {
      all: 'Tutto',
      burger: 'Hamburger',
      pizza: 'Pizza',
      postre: 'Dessert',
      pollo: 'Pollo',
      came: 'Carne',
      sushi: 'Sushi',
      marisco: 'Frutti di mare',
      taco: 'Taco',
      global: 'Internazionale',
      pan: 'Pane',
      vegano: 'Vegano',
    },
  },

  ru: {
    cartConfirm: 'Подтвердить корзину',
    cart: 'Корзина',
    pay: 'Оплатить',
    homeTitle: 'OLULO',
    searchPlaceholder: 'Поиск ресторана или блюда',
    chooseFavorites: 'Найти ресторан с самовывозом',
    pickupWaitlistBadge: 'Заказ после очереди',
    resultsCount: 'результатов',
    loading: 'Загрузка...',
    errorLoadingStores: 'Ошибка загрузки ресторанов',
    errorLoadingStoresMessage: 'Не удается подключиться к серверу. Повторите попытку.',
    noNearbyStores: 'Нет ресторанов поблизости',
    noNearbyStoresMessage: 'Не удалось найти рестораны в вашем текущем местоположении.',
    qrScanTitle: 'Сканировать QR-код',
    tableNumber: 'Стол',
    date: 'Дата',
    time: 'Время',
    menu: 'Меню',
    qty: 'Кол-во',
    price: 'Цена',
    selectTip: 'Выбрать чаевые',
    points: 'Баллы',
    available: 'Доступно',
    menuFirst: 'Сначала меню',
    use: 'Использовать',
    useAll: 'Использовать все',
    alwaysUseAll: 'Всегда использовать все',
    availablePoints: 'Доступные баллы',
    giftCard: 'Подарочная карта',
    enterCode: 'Введите код',
    apply: 'Применить',
    registerNewCard: 'Зарегистрировать новую карту',
    subtotal: 'Промежуточный итог',
    tax: 'Налог',
    tip: 'Чаевые',
    total: 'Итого',
    makePayment: 'Оплатить',
    storeStatus: {
      '영업중': 'Открыто',
      '준비중': 'Готовится',
      '마감': 'Закрыто',
    },
    sortBy: {
      rating: 'По рейтингу',
      distance: 'По расстоянию',
      name: 'По названию',
    },
    orders: {
      pickupOrdersTitle: 'Заказы на самовывоз',
      activeOrdersTitle: 'Активные заказы',
      loadingActiveOrders: 'Загрузка активных заказов.',
      activeOrdersLoadError: 'Не удалось загрузить активные заказы.',
      activeOrdersLoadErrorMessage: 'Повторите попытку через несколько минут.',
      noActiveOrders: 'Нет активных заказов.',
      authRequiredNotice: 'Для просмотра заказов требуется аутентификация.',
      viewOrderHistory: 'Просмотр истории заказов',
      tableLabel: 'Стол',
      pending: 'В ожидании',
      confirmed: 'Подтверждено',
      preparing: 'Готовится',
      ready: 'Готово к самовывозу',
      completed: 'Завершено',
      cancelled: 'Отменено',
      failed: 'Ошибка',
      order: 'Заказ',
      preparationProgress: 'Ход приготовления',
      status: 'Статус',
      estimatedTime: 'Ориентировочное время',
      readyNow: 'Готово к самовывозу!',
      minutesRemaining: 'мин осталось',
      pickupLocation: 'Место самовывоза',
      openMap: 'Открыть карту',
      items: 'Позиции',
      noOrders: 'Нет активных заказов на самовывоз.',
      timeline: 'История',
      cancelOrder: 'Отменить заказ',
      cancelConfirm: 'Вы уверены, что хотите отменить этот заказ?',
      refundPending: 'Возврат ожидает',
      refundPartial: 'Частичный возврат',
      refundFull: 'Возврат выполнен',
      cancelledByCustomer: 'Клиент',
      cancelledByStore: 'Ресторан',
      cancelledBySystem: 'Система',
      orderTypePickup: 'Самовывоз',
      orderTypeTable: 'За столом',
      paymentStatus: {
        unpaid: 'Не оплачено',
        paid: 'Оплачено',
        failed: 'Ошибка оплаты',
        cancelled: 'Оплата отменена',
        processing: 'Обрабатывается',
        refunded: 'Возврат выполнен',
        partialRefund: 'Частичный возврат',
      },
    },
    categories: {
      all: 'Все',
      burger: 'Бургер',
      pizza: 'Пицца',
      postre: 'Десерт',
      pollo: 'Курица',
      came: 'Мясо',
      sushi: 'Суши',
      marisco: 'Морепродукты',
      taco: 'Тако',
      global: 'Международная',
      pan: 'Хлеб',
      vegano: 'Веганская',
    },
  },

  hi: {
    cartConfirm: 'कार्ट पुष्टि करें',
    cart: 'कार्ट',
    pay: 'भुगतान करें',
    homeTitle: 'OLULO',
    searchPlaceholder: 'रेस्तरां या भोजन खोजें',
    chooseFavorites: 'पिकअप रेस्तरां खोजें',
    pickupWaitlistBadge: 'कतार के बाद ऑर्डर करें',
    resultsCount: 'परिणाम',
    loading: 'लोड हो रहा है...',
    errorLoadingStores: 'रेस्तरां लोड त्रुटि',
    errorLoadingStoresMessage: 'सर्वर से कनेक्ट नहीं हो सका। पुनः प्रयास करें।',
    noNearbyStores: 'पास में कोई रेस्तरां नहीं',
    noNearbyStoresMessage: 'आपके वर्तमान स्थान पर कोई रेस्तरां नहीं मिला।',
    qrScanTitle: 'QR कोड स्कैन करें',
    tableNumber: 'टेबल',
    date: 'तारीख',
    time: 'समय',
    menu: 'मेन्यू',
    qty: 'मात्रा',
    price: 'मूल्य',
    selectTip: 'टिप चुनें',
    points: 'अंक',
    available: 'उपलब्ध',
    menuFirst: 'मेन्यू पहले',
    use: 'उपयोग करें',
    useAll: 'सभी उपयोग करें',
    alwaysUseAll: 'हमेशा सभी उपयोग करें',
    availablePoints: 'उपलब्ध अंक',
    giftCard: 'गिफ्ट कार्ड',
    enterCode: 'कोड दर्ज करें',
    apply: 'लागू करें',
    registerNewCard: 'नया कार्ड पंजीकृत करें',
    subtotal: 'उप-योग',
    tax: 'कर',
    tip: 'टिप',
    total: 'कुल',
    makePayment: 'भुगतान करें',
    storeStatus: {
      '영업중': 'खुला',
      '준비중': 'तैयारी में',
      '마감': 'बंद',
    },
    sortBy: {
      rating: 'रेटिंग के अनुसार',
      distance: 'दूरी के अनुसार',
      name: 'नाम के अनुसार',
    },
    orders: {
      pickupOrdersTitle: 'पिकअप ऑर्डर',
      activeOrdersTitle: 'सक्रिय ऑर्डर',
      loadingActiveOrders: 'सक्रिय ऑर्डर लोड हो रहे हैं।',
      activeOrdersLoadError: 'सक्रिय ऑर्डर लोड नहीं हो सके।',
      activeOrdersLoadErrorMessage: 'कृपया कुछ देर बाद फिर कोशिश करें।',
      noActiveOrders: 'कोई सक्रिय ऑर्डर नहीं।',
      authRequiredNotice: 'अपने ऑर्डर देखने के लिए प्रमाणीकरण आवश्यक है।',
      viewOrderHistory: 'ऑर्डर इतिहास देखें',
      tableLabel: 'टेबल',
      pending: 'लंबित',
      confirmed: 'पुष्टि की गई',
      preparing: 'तैयारी में',
      ready: 'पिकअप के लिए तैयार',
      completed: 'पूर्ण',
      cancelled: 'रद्द',
      failed: 'विफल',
      order: 'ऑर्डर',
      preparationProgress: 'तैयारी की प्रगति',
      status: 'स्थिति',
      estimatedTime: 'अनुमानित समय',
      readyNow: 'अभी पिकअप के लिए तैयार!',
      minutesRemaining: 'मिनट शेष',
      pickupLocation: 'पिकअप स्थान',
      openMap: 'मानचित्र खोलें',
      items: 'आइटम',
      noOrders: 'कोई पिकअप ऑर्डर प्रगति में नहीं है।',
      timeline: 'समयरेखा',
      cancelOrder: 'ऑर्डर रद्द करें',
      cancelConfirm: 'क्या आप वाकई इस ऑर्डर को रद्द करना चाहते हैं?',
      refundPending: 'रिफंड लंबित',
      refundPartial: 'आंशिक रिफंड',
      refundFull: 'रिफंड पूर्ण',
      cancelledByCustomer: 'ग्राहक',
      cancelledByStore: 'रेस्तरां',
      cancelledBySystem: 'सिस्टम',
      orderTypePickup: 'पिकअप',
      orderTypeTable: 'टेबल',
      paymentStatus: {
        unpaid: 'अवैतनिक',
        paid: 'भुगतान हुआ',
        failed: 'भुगतान विफल',
        cancelled: 'भुगतान रद्द',
        processing: 'प्रक्रिया में',
        refunded: 'वापसी पूर्ण',
        partialRefund: 'आंशिक वापसी',
      },
    },
    categories: {
      all: 'सभी',
      burger: 'बर्गर',
      pizza: 'पिज़्ज़ा',
      postre: 'मिठाई',
      pollo: 'चिकन',
      came: 'मांस',
      sushi: 'सुशी',
      marisco: 'समुद्री भोजन',
      taco: 'टैको',
      global: 'अंतर्राष्ट्रीय',
      pan: 'ब्रेड',
      vegano: 'शाकाहारी',
    },
  },

  ar: {
    cartConfirm: 'تأكيد السلة',
    cart: 'السلة',
    pay: 'ادفع',
    homeTitle: 'OLULO',
    searchPlaceholder: 'البحث عن مطعم أو طعام',
    chooseFavorites: 'البحث عن مطعم للاستلام',
    pickupWaitlistBadge: 'اطلب بعد الانتظار في الطابور',
    resultsCount: 'نتائج',
    loading: 'جار التحميل...',
    errorLoadingStores: 'خطأ في تحميل المطاعم',
    errorLoadingStoresMessage: 'تعذر الاتصال بالخادم. يرجى المحاولة مرة أخرى.',
    noNearbyStores: 'لا توجد مطاعم قريبة',
    noNearbyStoresMessage: 'لم يتم العثور على مطاعم في موقعك الحالي.',
    qrScanTitle: 'مسح رمز QR',
    tableNumber: 'الطاولة',
    date: 'التاريخ',
    time: 'الوقت',
    menu: 'القائمة',
    qty: 'الكمية',
    price: 'السعر',
    selectTip: 'اختر الإكرامية',
    points: 'النقاط',
    available: 'متاح',
    menuFirst: 'القائمة أولاً',
    use: 'استخدم',
    useAll: 'استخدم الكل',
    alwaysUseAll: 'استخدم الكل دائماً',
    availablePoints: 'النقاط المتاحة',
    giftCard: 'بطاقة هدية',
    enterCode: 'أدخل الرمز',
    apply: 'تطبيق',
    registerNewCard: 'تسجيل بطاقة جديدة',
    subtotal: 'المجموع الفرعي',
    tax: 'الضريبة',
    tip: 'الإكرامية',
    total: 'الإجمالي',
    makePayment: 'ادفع',
    storeStatus: {
      '영업중': 'مفتوح',
      '준비중': 'قيد التحضير',
      '마감': 'مغلق',
    },
    sortBy: {
      rating: 'حسب التقييم',
      distance: 'حسب المسافة',
      name: 'حسب الاسم',
    },
    orders: {
      pickupOrdersTitle: 'طلبات الاستلام',
      activeOrdersTitle: 'الطلبات النشطة',
      loadingActiveOrders: 'جار تحميل الطلبات النشطة.',
      activeOrdersLoadError: 'تعذر تحميل الطلبات النشطة.',
      activeOrdersLoadErrorMessage: 'يرجى المحاولة مرة أخرى بعد قليل.',
      noActiveOrders: 'لا توجد طلبات نشطة.',
      authRequiredNotice: 'يلزم التحقق لعرض طلباتك.',
      viewOrderHistory: 'عرض سجل الطلبات',
      tableLabel: 'الطاولة',
      pending: 'قيد الانتظار',
      confirmed: 'مؤكد',
      preparing: 'قيد التحضير',
      ready: 'جاهز للاستلام',
      completed: 'مكتمل',
      cancelled: 'ملغي',
      failed: 'فشل',
      order: 'طلب',
      preparationProgress: 'تقدم التحضير',
      status: 'الحالة',
      estimatedTime: 'الوقت المتوقع',
      readyNow: 'جاهز للاستلام الآن!',
      minutesRemaining: 'دقيقة متبقية',
      pickupLocation: 'مكان الاستلام',
      openMap: 'فتح الخريطة',
      items: 'العناصر',
      noOrders: 'لا توجد طلبات استلام قيد التنفيذ.',
      timeline: 'الجدول الزمني',
      cancelOrder: 'إلغاء الطلب',
      cancelConfirm: 'هل أنت متأكد من رغبتك في إلغاء هذا الطلب؟',
      refundPending: 'استرداد معلق',
      refundPartial: 'استرداد جزئي',
      refundFull: 'تم الاسترداد',
      cancelledByCustomer: 'العميل',
      cancelledByStore: 'المطعم',
      cancelledBySystem: 'النظام',
      orderTypePickup: 'استلام',
      orderTypeTable: 'طاولة',
      paymentStatus: {
        unpaid: 'غير مدفوع',
        paid: 'مدفوع',
        failed: 'فشل الدفع',
        cancelled: 'تم إلغاء الدفع',
        processing: 'قيد المعالجة',
        refunded: 'تم الاسترداد',
        partialRefund: 'استرداد جزئي',
      },
    },
    categories: {
      all: 'الكل',
      burger: 'برجر',
      pizza: 'بيتزا',
      postre: 'حلوى',
      pollo: 'دجاج',
      came: 'لحم',
      sushi: 'سوشي',
      marisco: 'مأكولات بحرية',
      taco: 'تاكو',
      global: 'دولي',
      pan: 'خبز',
      vegano: 'نباتي',
    },
  },

  vi: {
    cartConfirm: 'Xác nhận giỏ hàng',
    cart: 'Giỏ hàng',
    pay: 'Thanh toán',
    homeTitle: 'OLULO',
    searchPlaceholder: 'Tìm cửa hàng hoặc món ăn',
    chooseFavorites: 'Tìm cửa hàng pickup',
    pickupWaitlistBadge: 'Đặt món sau khi xếp hàng',
    resultsCount: 'kết quả',
    loading: 'Đang tải...',
    errorLoadingStores: 'Lỗi tải cửa hàng',
    errorLoadingStoresMessage: 'Không thể kết nối đến máy chủ. Vui lòng thử lại.',
    noNearbyStores: 'Không có cửa hàng gần đây',
    noNearbyStoresMessage: 'Không tìm thấy cửa hàng tại vị trí hiện tại.',
    qrScanTitle: 'Quét mã QR',
    tableNumber: 'Bàn',
    date: 'Ngày',
    time: 'Giờ',
    menu: 'Thực đơn',
    qty: 'SL',
    price: 'Giá',
    selectTip: 'Chọn tiền tip',
    points: 'Điểm',
    available: 'Có sẵn',
    menuFirst: 'Thực đơn trước',
    use: 'Sử dụng',
    useAll: 'Dùng tất cả',
    alwaysUseAll: 'Luôn dùng tất cả',
    availablePoints: 'Điểm có sẵn',
    giftCard: 'Thẻ quà tặng',
    enterCode: 'Nhập mã',
    apply: 'Áp dụng',
    registerNewCard: 'Đăng ký thẻ mới',
    subtotal: 'Tạm tính',
    tax: 'Thuế',
    tip: 'Tip',
    total: 'Tổng',
    makePayment: 'Thanh toán',
    storeStatus: {
      '영업중': 'Đang mở',
      '준비중': 'Đang chuẩn bị',
      '마감': 'Đã đóng',
    },
    sortBy: {
      rating: 'Theo đánh giá',
      distance: 'Theo khoảng cách',
      name: 'Theo tên',
    },
    orders: {
      pickupOrdersTitle: 'Đơn hàng pickup',
      activeOrdersTitle: 'Đơn hàng đang thực hiện',
      loadingActiveOrders: 'Đang tải đơn hàng đang thực hiện.',
      activeOrdersLoadError: 'Không thể tải đơn hàng đang thực hiện.',
      activeOrdersLoadErrorMessage: 'Vui lòng thử lại sau giây lát.',
      noActiveOrders: 'Không có đơn hàng đang thực hiện.',
      authRequiredNotice: 'Cần xác thực để xem đơn hàng của bạn.',
      viewOrderHistory: 'Xem lịch sử đơn hàng',
      tableLabel: 'Bàn',
      pending: 'Đang chờ',
      confirmed: 'Đã xác nhận',
      preparing: 'Đang chuẩn bị',
      ready: 'Sẵn sàng nhận',
      completed: 'Hoàn thành',
      cancelled: 'Đã hủy',
      failed: 'Thất bại',
      order: 'Đơn hàng',
      preparationProgress: 'Tiến độ chuẩn bị',
      status: 'Trạng thái',
      estimatedTime: 'Thời gian dự kiến',
      readyNow: 'Sẵn sàng nhận ngay!',
      minutesRemaining: 'phút còn lại',
      pickupLocation: 'Địa điểm nhận',
      openMap: 'Mở bản đồ',
      items: 'Mặt hàng',
      noOrders: 'Không có đơn pickup nào đang xử lý.',
      timeline: 'Lịch sử',
      cancelOrder: 'Hủy đơn hàng',
      cancelConfirm: 'Bạn có chắc muốn hủy đơn hàng này?',
      refundPending: 'Đang chờ hoàn tiền',
      refundPartial: 'Hoàn tiền một phần',
      refundFull: 'Đã hoàn tiền',
      cancelledByCustomer: 'Khách hàng',
      cancelledByStore: 'Nhà hàng',
      cancelledBySystem: 'Hệ thống',
      orderTypePickup: 'Pickup',
      orderTypeTable: 'Tại bàn',
      paymentStatus: {
        unpaid: 'Chưa thanh toán',
        paid: 'Đã thanh toán',
        failed: 'Thanh toán thất bại',
        cancelled: 'Thanh toán bị hủy',
        processing: 'Đang xử lý',
        refunded: 'Đã hoàn tiền',
        partialRefund: 'Hoàn tiền một phần',
      },
    },
    categories: {
      all: 'Tất cả',
      burger: 'Burger',
      pizza: 'Pizza',
      postre: 'Tráng miệng',
      pollo: 'Gà',
      came: 'Thịt',
      sushi: 'Sushi',
      marisco: 'Hải sản',
      taco: 'Taco',
      global: 'Quốc tế',
      pan: 'Bánh mì',
      vegano: 'Chay',
    },
  },

  th: {
    cartConfirm: 'ยืนยันตะกร้า',
    cart: 'ตะกร้า',
    pay: 'ชำระเงิน',
    homeTitle: 'OLULO',
    searchPlaceholder: 'ค้นหาร้านหรืออาหาร',
    chooseFavorites: 'หาร้านสำหรับรับเอง',
    pickupWaitlistBadge: 'สั่งอาหารหลังต่อคิว',
    resultsCount: 'ผลลัพธ์',
    loading: 'กำลังโหลด...',
    errorLoadingStores: 'ข้อผิดพลาดในการโหลดร้าน',
    errorLoadingStoresMessage: 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ โปรดลองอีกครั้ง',
    noNearbyStores: 'ไม่มีร้านใกล้เคียง',
    noNearbyStoresMessage: 'ไม่พบร้านในตำแหน่งปัจจุบันของคุณ',
    qrScanTitle: 'สแกนคิวอาร์โค้ด',
    tableNumber: 'โต๊ะ',
    date: 'วันที่',
    time: 'เวลา',
    menu: 'เมนู',
    qty: 'จำนวน',
    price: 'ราคา',
    selectTip: 'เลือกทิป',
    points: 'คะแนน',
    available: 'พร้อมใช้งาน',
    menuFirst: 'เมนูก่อน',
    use: 'ใช้',
    useAll: 'ใช้ทั้งหมด',
    alwaysUseAll: 'ใช้ทั้งหมดเสมอ',
    availablePoints: 'คะแนนที่ใช้ได้',
    giftCard: 'บัตรของขวัญ',
    enterCode: 'กรอกรหัส',
    apply: 'ใช้',
    registerNewCard: 'ลงทะเบียนบัตรใหม่',
    subtotal: 'ยอดรวมย่อย',
    tax: 'ภาษี',
    tip: 'ทิป',
    total: 'ยอดรวม',
    makePayment: 'ชำระเงิน',
    storeStatus: {
      '영업중': 'เปิด',
      '준비중': 'กำลังเตรียม',
      '마감': 'ปิด',
    },
    sortBy: {
      rating: 'ตามคะแนน',
      distance: 'ตามระยะทาง',
      name: 'ตามชื่อ',
    },
    orders: {
      pickupOrdersTitle: 'คำสั่งซื้อรับเอง',
      activeOrdersTitle: 'คำสั่งซื้อที่กำลังดำเนินการ',
      loadingActiveOrders: 'กำลังโหลดคำสั่งซื้อที่กำลังดำเนินการ',
      activeOrdersLoadError: 'ไม่สามารถโหลดคำสั่งซื้อที่กำลังดำเนินการได้',
      activeOrdersLoadErrorMessage: 'โปรดลองอีกครั้งในอีกสักครู่',
      noActiveOrders: 'ไม่มีคำสั่งซื้อที่กำลังดำเนินการ',
      authRequiredNotice: 'ต้องยืนยันตัวตนเพื่อดูคำสั่งซื้อของคุณ',
      viewOrderHistory: 'ดูประวัติคำสั่งซื้อ',
      tableLabel: 'โต๊ะ',
      pending: 'รอดำเนินการ',
      confirmed: 'ยืนยันแล้ว',
      preparing: 'กำลังเตรียม',
      ready: 'พร้อมรับ',
      completed: 'เสร็จสิ้น',
      cancelled: 'ยกเลิก',
      failed: 'ล้มเหลว',
      order: 'คำสั่งซื้อ',
      preparationProgress: 'ความคืบหน้าการเตรียม',
      status: 'สถานะ',
      estimatedTime: 'เวลาโดยประมาณ',
      readyNow: 'พร้อมรับตอนนี้!',
      minutesRemaining: 'นาทีที่เหลือ',
      pickupLocation: 'สถานที่รับ',
      openMap: 'เปิดแผนที่',
      items: 'รายการ',
      noOrders: 'ไม่มีคำสั่งซื้อรับเองที่กำลังดำเนินการ',
      timeline: 'ไทม์ไลน์',
      cancelOrder: 'ยกเลิกคำสั่งซื้อ',
      cancelConfirm: 'คุณแน่ใจหรือไม่ว่าต้องการยกเลิกคำสั่งซื้อนี้?',
      refundPending: 'รอคืนเงิน',
      refundPartial: 'คืนเงินบางส่วน',
      refundFull: 'คืนเงินแล้ว',
      cancelledByCustomer: 'ลูกค้า',
      cancelledByStore: 'ร้านอาหาร',
      cancelledBySystem: 'ระบบ',
      orderTypePickup: 'รับเอง',
      orderTypeTable: 'ที่โต๊ะ',
      paymentStatus: {
        unpaid: 'ยังไม่ชำระ',
        paid: 'ชำระแล้ว',
        failed: 'การชำระล้มเหลว',
        cancelled: 'ยกเลิกการชำระ',
        processing: 'กำลังดำเนินการ',
        refunded: 'คืนเงินแล้ว',
        partialRefund: 'คืนเงินบางส่วน',
      },
    },
    categories: {
      all: 'ทั้งหมด',
      burger: 'เบอร์เกอร์',
      pizza: 'พิซซ่า',
      postre: 'ของหวาน',
      pollo: 'ไก่',
      came: 'เนื้อ',
      sushi: 'ซูชิ',
      marisco: 'อาหารทะเล',
      taco: 'ทาโก้',
      global: 'นานาชาติ',
      pan: 'ขนมปัง',
      vegano: 'มังสวิรัติ',
    },
  },

  nl: {
    cartConfirm: 'Winkelwagen bevestigen',
    cart: 'Winkelwagen',
    pay: 'Betalen',
    homeTitle: 'OLULO',
    searchPlaceholder: 'Zoek restaurant of gerecht',
    chooseFavorites: 'Vind afhaalrestaurant',
    pickupWaitlistBadge: 'Bestellen na de wachtrij',
    resultsCount: 'resultaten',
    loading: 'Laden...',
    errorLoadingStores: 'Fout bij laden restaurants',
    errorLoadingStoresMessage: 'Kan geen verbinding maken met server. Probeer opnieuw.',
    noNearbyStores: 'Geen restaurants in de buurt',
    noNearbyStoresMessage: 'Geen restaurants gevonden op uw huidige locatie.',
    qrScanTitle: 'QR-code scannen',
    tableNumber: 'Tafel',
    date: 'Datum',
    time: 'Tijd',
    menu: 'Menu',
    qty: 'Aantal',
    price: 'Prijs',
    selectTip: 'Fooi selecteren',
    points: 'Punten',
    available: 'Beschikbaar',
    menuFirst: 'Menu eerst',
    use: 'Gebruiken',
    useAll: 'Alles gebruiken',
    alwaysUseAll: 'Altijd alles gebruiken',
    availablePoints: 'Beschikbare punten',
    giftCard: 'Cadeaubon',
    enterCode: 'Code invoeren',
    apply: 'Toepassen',
    registerNewCard: 'Nieuwe kaart registreren',
    subtotal: 'Subtotaal',
    tax: 'BTW',
    tip: 'Fooi',
    total: 'Totaal',
    makePayment: 'Betalen',
    storeStatus: {
      '영업중': 'Geopend',
      '준비중': 'In voorbereiding',
      '마감': 'Gesloten',
    },
    sortBy: {
      rating: 'Op beoordeling',
      distance: 'Op afstand',
      name: 'Op naam',
    },
    orders: {
      pickupOrdersTitle: 'Afhaalbestellingen',
      activeOrdersTitle: 'Actieve bestellingen',
      loadingActiveOrders: 'Actieve bestellingen laden.',
      activeOrdersLoadError: 'Actieve bestellingen konden niet worden geladen.',
      activeOrdersLoadErrorMessage: 'Probeer het zo opnieuw.',
      noActiveOrders: 'Geen actieve bestellingen.',
      authRequiredNotice: 'Verificatie is vereist om uw bestellingen te bekijken.',
      viewOrderHistory: 'Bestelgeschiedenis bekijken',
      tableLabel: 'Tafel',
      pending: 'In afwachting',
      confirmed: 'Bevestigd',
      preparing: 'In voorbereiding',
      ready: 'Klaar voor afhalen',
      completed: 'Voltooid',
      cancelled: 'Geannuleerd',
      failed: 'Mislukt',
      order: 'Bestelling',
      preparationProgress: 'Voortgang voorbereiding',
      status: 'Status',
      estimatedTime: 'Geschatte tijd',
      readyNow: 'Nu klaar voor afhalen!',
      minutesRemaining: 'min resterend',
      pickupLocation: 'Afhaallocatie',
      openMap: 'Kaart openen',
      items: 'Items',
      noOrders: 'Geen afhaalbestellingen in behandeling.',
      timeline: 'Tijdlijn',
      cancelOrder: 'Bestelling annuleren',
      cancelConfirm: 'Weet u zeker dat u deze bestelling wilt annuleren?',
      refundPending: 'Terugbetaling in afwachting',
      refundPartial: 'Gedeeltelijke terugbetaling',
      refundFull: 'Terugbetaald',
      cancelledByCustomer: 'Klant',
      cancelledByStore: 'Restaurant',
      cancelledBySystem: 'Systeem',
      orderTypePickup: 'Afhaal',
      orderTypeTable: 'Aan tafel',
      paymentStatus: {
        unpaid: 'Niet betaald',
        paid: 'Betaald',
        failed: 'Betaling mislukt',
        cancelled: 'Betaling geannuleerd',
        processing: 'Wordt verwerkt',
        refunded: 'Terugbetaald',
        partialRefund: 'Gedeeltelijke terugbetaling',
      },
    },
    categories: {
      all: 'Alles',
      burger: 'Burger',
      pizza: 'Pizza',
      postre: 'Dessert',
      pollo: 'Kip',
      came: 'Vlees',
      sushi: 'Sushi',
      marisco: 'Zeevruchten',
      taco: 'Taco',
      global: 'Internationaal',
      pan: 'Brood',
      vegano: 'Vegan',
    },
  },
};

/**
 * 번역 조회 — #460 fallback chain.
 *
 * 우선순위: locale → en → es → ko (deep merge).
 * 신규 locale (ar/de/fr/hi/it/ja/nl/pt/ru/th/vi/zh) 의 partial 엔트리는
 * top-level keys 만 정의하고, 누락된 키는 fallback chain 으로 채움.
 *
 * Language 타입 (deprecated 3개 언어) 호환 유지 — 기존 호출자 시그니처 보존.
 */
export const getTranslation = (locale: Locale | Language): TranslationStrings => {
  const baseEn = translations.en;
  const partial = (translations as Translations)[locale as Locale];

  if (locale === 'ko' || locale === 'es' || locale === 'en') {
    return translations[locale];
  }

  if (!partial) {
    return baseEn;
  }

  // top-level merge — nested 객체는 partial 에 부분 정의가 없으면 en 의 full 객체 사용.
  // R1 codex 시정: restaurants 는 2단계 nested (Record<key, {name, cuisine}>) → leaf 단위 merge
  // 로 sibling fallback 손실 방지.
  const partialPartial = partial as Partial<TranslationStrings>;
  const mergedRestaurants: TranslationStrings['restaurants'] = { ...baseEn.restaurants };
  if (partialPartial.restaurants) {
    for (const [key, value] of Object.entries(partialPartial.restaurants)) {
      mergedRestaurants[key] = { ...baseEn.restaurants[key], ...value };
    }
  }

  return {
    ...baseEn,
    ...partial,
    storeStatus: { ...baseEn.storeStatus, ...partialPartial.storeStatus },
    sortBy: { ...baseEn.sortBy, ...partialPartial.sortBy },
    orders: { ...baseEn.orders, ...partialPartial.orders },
    categories: { ...baseEn.categories, ...partialPartial.categories },
    restaurants: mergedRestaurants,
  };
};
